import {
  ChecklistPhase,
  InvestmentChecklistDTO,
  InvestmentChecklistTaskDTO,
  ChecklistDueAnchor,
} from '@inversor-hub/types';
import { PHASE_ORDER } from './checklist-constants';
import dayjs from 'dayjs';

export interface PhaseProgress {
  phase: ChecklistPhase;
  completed: number;
  total: number;
  percentage: number;
  overdue: number;
}

export function calculatePhaseProgress(
  tasks: InvestmentChecklistTaskDTO[],
  phase: ChecklistPhase
): PhaseProgress {
  const notSkippedPhaseTasks = getTasksByPhase(tasks, phase).filter(
    (task) => task.status !== 'SKIPPED'
  );
  const completed = notSkippedPhaseTasks.filter((task) => task.status === 'DONE').length;
  const total = notSkippedPhaseTasks.length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const overdue = notSkippedPhaseTasks.filter((task) => isTaskOverdue(task)).length;

  return { phase, completed, total, percentage, overdue };
}

export function calculateOverallProgress(checklist: InvestmentChecklistDTO) {
  const notSkippedTasks = checklist.tasks.filter((task) => task.status !== 'SKIPPED');
  const completed = notSkippedTasks.filter((task) => task.status === 'DONE').length;
  const total = notSkippedTasks.length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { completed, total, percentage };
}

export function getOverdueTasks(tasks: InvestmentChecklistTaskDTO[]): InvestmentChecklistTaskDTO[] {
  return tasks.filter(isTaskOverdue);
}

export function isTaskOverdue(task: InvestmentChecklistTaskDTO): boolean {
  if (!task.dueAt) {
    return false;
  }
  const now = new Date();
  const dueDate = new Date(task.dueAt);
  return dueDate < now && task.status !== 'DONE' && task.status !== 'SKIPPED';
}

export function getNextDueTask(
  tasks: InvestmentChecklistTaskDTO[]
): InvestmentChecklistTaskDTO | undefined {
  const pendingTasksWithDue = tasks.filter((task) => task.dueAt && task.status === 'PENDING');

  if (pendingTasksWithDue.length === 0) {
    return undefined;
  }

  return pendingTasksWithDue.reduce((earliest, current) => {
    const earliestDate = new Date(earliest.dueAt!);
    const currentDate = new Date(current.dueAt!);
    return currentDate < earliestDate ? current : earliest;
  });
}

export function getCurrentPhase(checklist: InvestmentChecklistDTO): ChecklistPhase | 'COMPLETE' {
  for (const phase of PHASE_ORDER) {
    const phaseTasks = checklist.tasks.filter((task) => task.phase === phase);
    if (phaseTasks.length === 0) {
      continue;
    }
    const hasPending = phaseTasks.some((task) => task.status === 'PENDING');
    if (hasPending) {
      return phase;
    }
  }
  return 'COMPLETE';
}

export function formatDueDate(dueAt: string): string {
  return dayjs(dueAt).format('DD/MM/YYYY');
}

export function getDueDateStatus(dueAt?: string): 'overdue' | 'soon' | 'ok' | 'none' {
  if (!dueAt) {
    return 'none';
  }

  const now = dayjs();
  const due = dayjs(dueAt);
  const diff = due.diff(now, 'day');

  if (diff < 0) {
    return 'overdue';
  }
  if (diff <= 3) {
    return 'soon';
  }
  return 'ok';
}

export function getDaysOverdue(dueAt: string): number {
  const now = dayjs();
  const due = dayjs(dueAt);
  return now.diff(due, 'day');
}

export function getTasksByPhase(
  tasks: InvestmentChecklistTaskDTO[],
  phase: ChecklistPhase
): InvestmentChecklistTaskDTO[] {
  return tasks.filter((task) => task.phase === phase).sort((a, b) => a.order - b.order);
}

export function formatAnchorDateLabel(anchor?: ChecklistDueAnchor, offsetDays?: number): string {
  if (!anchor) {
    return '';
  }

  const anchorLabels: Record<ChecklistDueAnchor, string> = {
    SIGNING_DATE: 'firma de arras',
    CLOSING_DATE: 'cierre (CV)',
    MOVE_IN_DATE: 'entrada',
    FEIN_DATE: 'FEIN',
    ARRAS_EXPIRY_DATE: 'expiración de arras',
  };

  const anchorLabel = anchorLabels[anchor];

  if (!offsetDays || offsetDays === 0) {
    return `Día de ${anchorLabel}`;
  }

  if (offsetDays > 0) {
    return `${offsetDays} días hábiles después de ${anchorLabel}`;
  }

  return `${Math.abs(offsetDays)} días hábiles antes de ${anchorLabel}`;
}

const SKIPPABLE_PHASES: ChecklistPhase[] = ['MORTGAGE', 'REFURB'];

export function canSkipPhase(phase: ChecklistPhase): boolean {
  return SKIPPABLE_PHASES.includes(phase);
}

export function isPhaseSkipped(
  tasks: InvestmentChecklistTaskDTO[],
  phase: ChecklistPhase
): boolean {
  const phaseTasks = getTasksByPhase(tasks, phase);
  if (phaseTasks.length === 0) {
    return false;
  }
  return phaseTasks.every((task) => task.status === 'SKIPPED');
}
